import {
	Box,
	Checkbox,
	Container,
	ListItemText,
	MenuItem,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import moment from 'moment';

import { getExportData } from 'common/api';
import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import ModalEle from 'common/components/Modal/ModalEle';
import { ISelectOption } from 'common/components/MulltipleSelectCheckmarks/MultipleSelectCheckmarks';
import {
	EXPORT_EXCEL_NAME_DATE_FORMAT,
	EXPORT_FILE_TYPE,
	MODULES,
} from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	capitalizeName,
	downloadCsvFileData,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { getExportPayloadForHorizonScanning } from 'features/HorizonScanning/horizonScanUtils';
import { getExportPayloadForRequirementInventory } from 'features/RegulationInventory/RegInventoryUtils';
import { generateExportPayloadForRiskReferenceTaxonomy } from 'features/RiskProfile/Taxonomy/RiskReferenceTaxonomy/RiskRefTaxonomyUtils';

import styles from './ExportData.module.scss';

interface ExportBoxProps {
	onCloseModal: () => void;
	exportTypeName?: string;
	optionsData: ISelectOption[];
	exportedCount: number;
	refFilterData?: any;
	refSearchText?: any;
	source?: string;
	colNamesMapping?: any;
	isDataLoading?: boolean;
	moduleType: string;
}

const { checkboxcheckedcolor, checkboxuncheckedcolor } = styles;
const sxBoxProps = { width: '30rem', height: '27.5rem', margin: 'auto' };
const {
	RISK_REFERENCE_TAXONOMY,
	REQUIREMENT_INVENTORY,
	HORIZON_SCAN_AND_ALERTS,
} = MODULES;
const { OK } = HTTP_STATUS_CODES;
const { EXCEL } = EXPORT_FILE_TYPE;

const ExportData = (props: ExportBoxProps) => {
	const {
		onCloseModal,
		exportTypeName,
		optionsData,
		exportedCount,
		refFilterData,
		refSearchText,
		source,
		colNamesMapping,
		isDataLoading,
		moduleType,
	} = props;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isDownloadDisabled, setIsDownloadDisabled] = useState<boolean>(true);
	const [options, setOptions] = useState<ISelectOption[]>(optionsData);
	const [allChecked, setAllChecked] = useState<boolean>(false);
	const dispatch = useDispatch();

	useEffect(() => {
		setIsDownloadDisabled(options.filter((item) => item.checked).length < 1);
	}, [options]);

	useEffect(() => {
		setOptions(optionsData);
	}, [optionsData]);

	const handleCheckedChange = (checked: boolean, option: ISelectOption) => {
		setOptions((prevstate: ISelectOption[]) => {
			const updatedOptions = prevstate?.map((element: ISelectOption) => {
				if (element.value === option.value) {
					element.checked = checked;
				}
				return element;
			});
			return updatedOptions;
		});
	};

	const handleParentClick = (option: ISelectOption) => {
		setAllChecked(false);
		const selectedMenuItem = options.find((ele) => ele.value === option.value);
		const newValue = !selectedMenuItem?.checked;
		handleCheckedChange(newValue, option);
	};

	const cancelHandler = useCallback(() => {
		onCloseModal();
	}, []);

	const generateColNames = (selectedColKeys: any) =>
		selectedColKeys.map((key: string) => colNamesMapping[key]);

	const getPayload = (moduleType: string) => {
		switch (moduleType) {
			case RISK_REFERENCE_TAXONOMY:
				return generateExportPayloadForRiskReferenceTaxonomy(
					options,
					source,
					exportTypeName,
					refSearchText,
					refFilterData,
					exportedCount,
				);
			case REQUIREMENT_INVENTORY:
				return getExportPayloadForRequirementInventory(
					options,
					refSearchText,
					refFilterData,
					exportedCount,
					exportTypeName || '',
				);
			case HORIZON_SCAN_AND_ALERTS:
				return getExportPayloadForHorizonScanning(
					options,
					refSearchText,
					refFilterData,
					exportedCount,
					exportTypeName || '',
				);
			default:
				return {};
		}
	};

	const parseAPIResponse = (payloadRes: any, moduleType: string, res: any) => {
		switch (moduleType) {
			case RISK_REFERENCE_TAXONOMY: {
				const sortedColNames = generateColNames(payloadRes.selectedColKeys).map(
					(name: string) => toSentenceCase(name),
				);
				downloadCsvFileData(
					res?.responseBody.content,
					`${capitalizeName(exportTypeName || '')}_${moment(new Date()).format(
						EXPORT_EXCEL_NAME_DATE_FORMAT,
					)}`,
					EXCEL,
					colNamesMapping ? sortedColNames : undefined,
					payloadRes.selectedColKeys,
				);
				break;
			}
			case REQUIREMENT_INVENTORY: {
				const checkedOptionsLabel = options
					.filter((res) => res.checked)
					.map((res) => res.label);
				const checkedOptionsValue = options
					.filter((res) => res.checked)
					.map((res) => res.value);

				downloadCsvFileData(
					res?.responseBody.content,
					`${capitalizeName(exportTypeName || '')}_${moment(new Date()).format(
						EXPORT_EXCEL_NAME_DATE_FORMAT,
					)}`,
					EXCEL,
					checkedOptionsLabel,
					checkedOptionsValue,
				);
				break;
			}
			case HORIZON_SCAN_AND_ALERTS: {
				const data = res?.responseBody.content;
				if (!data) return;

				const dataWithRecordIDs = data.map((item: any, index: number) => ({
					recordID: index + 1,
					...item,
				}));
				const checkedOptionsLabel = options
					.filter((res) => res.checked)
					.map((res) => res.label);
				const checkedOptionsValue = options
					.filter((res) => res.checked)
					.map((res) => res.value);

				const columnHeaders = ['Record ID', ...checkedOptionsLabel];
				const fieldNames = ['recordID', ...checkedOptionsValue];

				downloadCsvFileData(
					dataWithRecordIDs,
					`${capitalizeName(exportTypeName || '')}_${moment(new Date()).format(
						EXPORT_EXCEL_NAME_DATE_FORMAT,
					)}`,
					EXCEL,
					columnHeaders,
					fieldNames,
				);
				break;
			}
			default:
				break;
		}
	};

	const downloadFile = async () => {
		setIsLoading(true);
		const payloadRes: any = getPayload(moduleType);
		const res = await getExportData(payloadRes.payload, moduleType);
		if (res?.statusCode === OK && res?.responseBody) {
			parseAPIResponse(payloadRes, moduleType, res);
		} else {
			showErrorMessage(res);
		}
		setIsLoading(false);
		onCloseModal();
	};

	const showErrorMessage = (res: any) => {
		dispatch(
			setToastMessage({
				message: res?.errorBody?.errorMessage || 'Failed to download.',
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};

	const handleSelectAll = () => {
		handleSelectAllChange(!allChecked);
	};

	const handleSelectAllChange = (checked: boolean) => {
		setAllChecked(checked);
		setOptions((prevstate: ISelectOption[]) => {
			const updatedOptions = prevstate?.map((element: ISelectOption) => {
				element.checked = checked;
				return element;
			});
			return updatedOptions;
		});
	};

	const isAllCheckBoxesSelected = options.every((res) => res.checked);

	const renderOptionEle = (
		option: any,
		parentEleHandler: (arg0?: any) => void,
		childEleHandler: (arg0: boolean, arg1?: any) => void,
	) => (
		<MenuItem
			key={option ? option.value : 'all'}
			className={styles['menu-item']}
			value={option ? option.value : toSentenceCase('Select All')}
			data-testid={`menu_${option ? option.value : 'all'}`}
			onClick={(event) => {
				event.stopPropagation();
				if (option) {
					parentEleHandler(option);
				} else {
					parentEleHandler();
				}
			}}
		>
			<Checkbox
				data-testid={`checkbox_${option ? option.value : 'all'}`}
				className={styles['menu-item']}
				sx={{
					color: checkboxuncheckedcolor,
					'&.Mui-checked': { color: checkboxcheckedcolor },
					'&.MuiCheckbox-indeterminate': { color: checkboxcheckedcolor },
				}}
				checked={
					option
						? option?.checked || allChecked
						: allChecked || isAllCheckBoxesSelected
				}
				indeterminate={option?.indeterminate}
				onChange={(event) => {
					event.stopPropagation();
					childEleHandler(event.target.checked, option);
				}}
			/>
			{option ? (
				<Tooltip
					title={option.label}
					arrow
					onClick={(event) => {
						event.stopPropagation();
						parentEleHandler(option);
					}}
				>
					<ListItemText
						className={styles['text-limit']}
						primary={
							option.label === 'AI Summary'
								? 'AI summary'
								: toSentenceCase(option?.label)
						}
					/>
				</Tooltip>
			) : (
				toSentenceCase('Select All')
			)}
		</MenuItem>
	);

	return (
		<ModalEle open handleClose={onCloseModal} sxBoxProps={sxBoxProps}>
			<Container className={styles['add-container']}>
				<Stack direction="column">
					<Stack direction="row" justifyContent="space-between">
						<Typography className={styles['header-text']} data-testid="header">
							Download data as Excel
						</Typography>
						<CloseIcon
							fontSize="medium"
							onClick={onCloseModal}
							className={styles['add-closemodal']}
							data-testid="close-icon"
						/>
					</Stack>
					<Box className={styles['export-text-area']} data-testid="text-area">
						<Typography className={styles['export-text']}>
							Select the column(s) to include in spreadsheet.
						</Typography>
					</Box>
				</Stack>
				{isLoading || isDataLoading ? (
					<Loader show />
				) : (
					<div className={styles['add-dropdown-area']}>
						{renderOptionEle(null, handleSelectAll, handleSelectAllChange)}
						<div className={styles['options-wrapper']}>
							{options.map((option: ISelectOption) => (
								<div key={option.value} data-testid="parent-checkbox-testid">
									{renderOptionEle(
										option,
										handleParentClick,
										handleCheckedChange,
									)}
								</div>
							))}
						</div>
					</div>
				)}
				<Box className={styles['btn-box']}>
					<Stack
						direction="row"
						justifyContent="flex-end"
						className={styles['btn-grp']}
					>
						<ButtonComp
							btnType="secondary"
							clickHandler={cancelHandler}
							data-testid="cancel-btn"
						>
							Cancel
						</ButtonComp>
						<ButtonComp
							btnType="primary"
							clickHandler={downloadFile}
							disabled={isDownloadDisabled || isLoading || isDataLoading}
							data-testid="download-btn"
						>
							Download
						</ButtonComp>
					</Stack>
				</Box>
			</Container>
		</ModalEle>
	);
};

export default ExportData;
